"use client";

import { Branding, BrandNames } from "@hopper-b2b/types";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/en-gb";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import "dayjs/locale/fr";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import * as countries from "i18n-iso-countries";
import enCountries from "i18n-iso-countries/langs/en.json";
import esCountries from "i18n-iso-countries/langs/es.json";
import ptCountries from "i18n-iso-countries/langs/pt.json";
import frCountries from "i18n-iso-countries/langs/fr.json";
import i18next from "i18next";
import { initReactI18next, I18nextProvider } from "react-i18next";
import { FC, ReactNode, useEffect, useMemo } from "react";
import en_translations from "../locales/en/index";
import es_translations from "../locales/es/index";
import pt_translations from "../locales/pt/index";
import fr_translations from "../locales/fr/index";
import { I18nNamespace, Translation, TranslationLanguage } from "./types";
import { getRootLang } from "./utils/getRootLang";
import { getLangFromStorage } from "./utils/getLang";
import { I18nContext } from "./I18nContext";

// See https://github.com/i18next/react-i18next/issues/1587#issuecomment-1328676628
declare module "i18next" {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

export interface iI18nProvider {
  children: ReactNode;
  branding: Branding;
  defaultLng: string;
  localeParam?: string;
  tenantTranslation?: Translation;
}

export const I18nProvider: FC<iI18nProvider> = ({
  children,
  defaultLng,
  branding,
  tenantTranslation = {},
}: iI18nProvider) => {
  // Deciding the language to use to initialize i18next
  //
  // It first looks at the user's preference stored either in the localStorage
  // or in the browser settings (getLangFromStorage) and check if the language is supported
  // by the app/tenant. Note that here it checks the whole tag of the language e.g. en-AU or es-MX.
  // LocalStorage can have a language tag when a language was selected by the language selector.
  //
  // If it is not supported, it tries the "language" part of the value (i.e. "en" for "en-AU" or
  // "es" for "es-MX") and check if it is supported.
  //
  // If it is still not supported, it uses the `defaultLng` and does the same check.
  //
  // Finally it uses the "language" part of the `defaultLng` which is the legacy behavior.
  const lang = useMemo(() => {
    function isLanguageSupported(lang: string): boolean {
      return (
        lang in TranslationLanguage &&
        !!branding[lang] &&
        !!(branding[lang] as BrandNames)?.supportedLanguages?.some(
          (entry) => entry.key === lang
        )
      );
    }

    const userLang = getLangFromStorage();
    if (userLang && isLanguageSupported(userLang)) {
      return userLang;
    }
    const userLangRoot = userLang ? getRootLang(userLang) : null;
    if (
      userLangRoot &&
      userLangRoot !== userLang &&
      isLanguageSupported(userLangRoot)
    ) {
      return userLangRoot;
    }
    if (isLanguageSupported(defaultLng)) {
      return defaultLng;
    }
    return getRootLang(defaultLng);
  }, [defaultLng, branding]);

  const translationLanguage = useMemo(() => {
    // Look up supported languages in the following order:
    // `lang` -> branding.default -> "en"
    if (lang in TranslationLanguage) {
      return TranslationLanguage[lang as TranslationLanguage];
    }
    if (branding.default && branding.default in TranslationLanguage) {
      return TranslationLanguage[branding.default as TranslationLanguage];
    }
    return TranslationLanguage.en;
  }, [lang, branding.default]);

  // Create and initialize i18next instance synchronously during render
  // This ensures translations are available on the first render
  const i18nInstance = useMemo(() => {
    const instance = i18next.createInstance();

    const fallbackLng = branding.default || "en";

    // Initialize with empty resources first, then add resources via addResourceBundle
    // to ensure deep merging of nested translation objects
    instance.use(initReactI18next).init({
      resources: {},
      lng: translationLanguage,
      fallbackLng,
      defaultNS: I18nNamespace.translation,
      ns: [I18nNamespace.translation, I18nNamespace.brand],
      returnNull: false,
      initImmediate: false,
    });

    // Deep merge the values in the namespace.
    const deep = true;
    // Overwrite the values in the namespace.
    const overwrite = true;

    // Add resources for a language using addResourceBundle (deep merge)
    function addResourceBundlesForLanguage(language: string) {
      // Brand namespace
      instance.addResourceBundle(
        language,
        I18nNamespace.brand,
        { ...(language in branding ? (branding[language] as BrandNames) : {}) },
        deep,
        overwrite
      );

      // Translation namespace - add base translations first
      const [translation] = getTranslations(language);
      instance.addResourceBundle(
        language,
        I18nNamespace.translation,
        translation,
        deep,
        overwrite
      );

      // Then add tenant translations (deep merge will preserve nested keys)
      const tenantLangTranslation =
        tenantTranslation[language as TranslationLanguage];
      if (tenantLangTranslation) {
        instance.addResourceBundle(
          language,
          I18nNamespace.translation,
          tenantLangTranslation,
          deep,
          overwrite
        );
      }
    }

    // Add resources for the primary language and fallback
    addResourceBundlesForLanguage(translationLanguage);
    if (fallbackLng !== translationLanguage) {
      addResourceBundlesForLanguage(fallbackLng);
    }

    // Add daterange formatter
    const formatter = instance.services.formatter;
    if (formatter) {
      formatter.addCached("daterange", (lng, options) => {
        const format = new Intl.DateTimeFormat(lng, {
          month: "short",
          day: "numeric",
          ...(options as Intl.DateTimeFormatOptions),
        });
        return (val) => {
          const dates = val as unknown[];
          return format.formatRange(dates[0] as Date, dates[1] as Date);
        };
      });
    }

    return instance;
    // Note: We intentionally don't include branding and tenantTranslation here
    // to avoid recreating the instance on every change. Dynamic updates are
    // handled in the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, translationLanguage, branding.default]);

  // Update resources dynamically when branding or tenant translations change after init
  useEffect(() => {
    // Deep merge the values in the namespace.
    const deep = true;
    // Overwrite the values in the namespace.
    const overwrite = true;

    function addResourceBundles(language: string) {
      //Brand namespace
      i18nInstance.addResourceBundle(
        language,
        I18nNamespace.brand,
        { ...(language in branding ? (branding[language] as BrandNames) : {}) },
        deep,
        overwrite
      );

      //Translation namespace
      const [translation] = getTranslations(language);
      i18nInstance.addResourceBundle(
        language,
        I18nNamespace.translation,
        translation,
        deep,
        overwrite
      );

      const tenantLangTranslation =
        tenantTranslation[language as TranslationLanguage];
      if (tenantLangTranslation) {
        i18nInstance.addResourceBundle(
          language,
          I18nNamespace.translation,
          tenantLangTranslation,
          deep,
          overwrite
        );
      }
    }

    addResourceBundles(translationLanguage);
    // Load the resources from fallback language
    const fallbackLngOption = i18nInstance.options.fallbackLng;
    let fallbackLng: string | undefined;
    if (typeof fallbackLngOption === "string") {
      fallbackLng = fallbackLngOption;
    } else if (
      Array.isArray(fallbackLngOption) &&
      fallbackLngOption.length > 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const firstLang = fallbackLngOption[0];
      if (typeof firstLang === "string") {
        fallbackLng = firstLang;
      }
    }
    if (
      fallbackLng &&
      (fallbackLng as TranslationLanguage) !== translationLanguage
    ) {
      addResourceBundles(fallbackLng);
    }
  }, [
    // TODO: Find better way to trigger useEffect.
    // Note: As these are objects, dynamically updating values in the object would not trigger use effect.
    // No usecase currently
    i18nInstance,
    branding,
    branding.en,
    branding.es,
    branding.pt,
    branding.fr,
    tenantTranslation,
    tenantTranslation.en,
    tenantTranslation.es,
    tenantTranslation.pt,
    tenantTranslation.fr,
    translationLanguage,
  ]);

  useEffect(() => {
    const [, countryList] = getTranslations(lang);
    countries.registerLocale(countryList);
  }, [lang]);

  // Set dayjs locale
  useEffect(() => {
    dayjs.locale(lang);
    dayjs.extend(LocalizedFormat);
  }, [lang]);

  return (
    <I18nContext.Provider value={i18nInstance}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */}
      <I18nextProvider i18n={i18nInstance as any}>{children}</I18nextProvider>
    </I18nContext.Provider>
  );
};

export const getTranslations = (
  language: string
): [unknown, countries.LocaleData] => {
  switch (language as TranslationLanguage) {
    case TranslationLanguage.pt:
      return [pt_translations, ptCountries];
    case TranslationLanguage.es:
      return [es_translations, esCountries];
    case TranslationLanguage.en:
      return [en_translations, enCountries];
    case TranslationLanguage.fr:
      return [fr_translations, frCountries];
    default:
      return [en_translations, enCountries];
  }
};
